'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  User, Edit3, Lock, LogOut, Moon, Sun, CheckCircle, X,
  Shield, Clock, CreditCard, DollarSign, Bell, Eye, EyeOff
} from 'lucide-react';

const activityLog = [
  { id: 1, action: 'Approved loan VL-2026-005 for Grace Vira', time: '2026-08-05 09:14', type: 'loan' },
  { id: 2, action: 'Recorded repayment RCP-003 — Sam Kaltonga', time: '2026-08-04 14:32', type: 'repayment' },
  { id: 3, action: 'Added new borrower: Mele Tari', time: '2026-08-04 11:05', type: 'borrower' },
  { id: 4, action: 'Applied late fee to VL-2026-004', time: '2026-08-03 16:20', type: 'fee' },
  { id: 5, action: 'Recorded capital contribution — Mere Vanuatu', time: '2026-08-03 10:45', type: 'capital' },
  { id: 6, action: 'Updated loan rules — default rate changed to 20%', time: '2026-08-02 09:00', type: 'settings' },
  { id: 7, action: 'Created new user: Rex Naupa (Collector)', time: '2026-08-01 15:30', type: 'user' },
];

const activityIcon = (type: string) => {
  const map: Record<string, React.ReactNode> = {
    loan: <CreditCard size={13} className="text-positive" />,
    repayment: <DollarSign size={13} className="text-info" />,
    borrower: <User size={13} className="text-primary" />,
    fee: <Bell size={13} className="text-warning" />,
    capital: <Shield size={13} className="text-positive" />,
    settings: <Edit3 size={13} className="text-muted-foreground" />,
    user: <User size={13} className="text-info" />,
  };
  return map[type] || <Clock size={13} className="text-muted-foreground" />;
};

type ModalType = 'edit' | 'password' | null;

export default function ProfilePage() {
  const [modal, setModal] = useState<ModalType>(null);
  const [darkMode, setDarkMode] = useState(true);
  const [profile, setProfile] = useState({ name: 'Siosaia Taufa', email: 'siosaia@vatulend.vu', phone: '+678 555 0101', role: 'Admin', joinDate: '2024-01-15' });
  const [editForm, setEditForm] = useState({ ...profile });
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setProfile({ ...editForm });
    setSaved(true);
    setTimeout(() => { setSaved(false); setModal(null); }, 1500);
  };

  return (
    <AppLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">User Profile</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your account, security, and activity history</p>
        </div>

        {/* Profile Card */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-primary/15 border-2 border-primary/30 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">{profile.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-positive border-2 border-card flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-positive-foreground" />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-foreground">{profile.name}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">{profile.role}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{profile.email}</p>
              <p className="text-xs text-muted-foreground mt-1">Member since {profile.joinDate} · {profile.phone}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => { setEditForm({ ...profile }); setModal('edit'); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                <Edit3 size={14} /> Edit Profile
              </button>
              <button onClick={() => setModal('password')} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
                <Lock size={14} /> Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                {darkMode ? <Moon size={16} className="text-info" /> : <Sun size={16} className="text-warning" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Theme</p>
                <p className="text-xs text-muted-foreground">{darkMode ? 'Dark mode' : 'Light mode'}</p>
              </div>
            </div>
            <button
              onClick={() => setDarkMode(d => !d)}
              className={`relative w-11 h-6 rounded-full transition-colors ${darkMode ? 'bg-primary' : 'bg-muted'}`}
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${darkMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
              <Shield size={16} className="text-positive" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Security</p>
              <p className="text-xs text-positive">2FA Enabled</p>
            </div>
          </div>

          <a href="/sign-up-login-screen" className="bg-card border border-negative/20 rounded-xl p-4 flex items-center gap-3 hover:bg-negative/5 transition-colors group">
            <div className="w-9 h-9 rounded-lg bg-negative/10 flex items-center justify-center">
              <LogOut size={16} className="text-negative" />
            </div>
            <div>
              <p className="text-sm font-semibold text-negative">Sign Out</p>
              <p className="text-xs text-muted-foreground">End session</p>
            </div>
          </a>
        </div>

        {/* Activity Log */}
        <div className="bg-card border border-border rounded-xl">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <Clock size={15} className="text-primary" />
            <h2 className="font-semibold text-foreground text-sm">Administrative Activity Log</h2>
          </div>
          <div className="divide-y divide-border">
            {activityLog.map(log => (
              <div key={log.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  {activityIcon(log.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{log.action}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{log.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-bold text-foreground">
                {modal === 'edit' ? 'Edit Profile' : 'Change Password'}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>

            {modal === 'edit' && (
              <div className="p-6 space-y-4">
                {[
                  { label: 'Full Name', key: 'name' },
                  { label: 'Email', key: 'email' },
                  { label: 'Phone', key: 'phone' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{f.label}</label>
                    <input value={(editForm as any)[f.key]} onChange={e => setEditForm(ef => ({ ...ef, [f.key]: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                  <button onClick={handleSave} className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${saved ? 'bg-positive text-primary-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                    {saved ? <><CheckCircle size={14} /> Saved!</> : <><CheckCircle size={14} /> Save Changes</>}
                  </button>
                </div>
              </div>
            )}

            {modal === 'password' && (
              <div className="p-6 space-y-4">
                {[
                  { label: 'Current Password', key: 'current' as const },
                  { label: 'New Password', key: 'new' as const },
                  { label: 'Confirm New Password', key: 'confirm' as const },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{f.label}</label>
                    <div className="relative">
                      <input
                        type={(showPwd as any)[f.key] ? 'text' : 'password'}
                        value={(pwdForm as any)[f.key === 'new' ? 'newPwd' : f.key]}
                        onChange={e => setPwdForm(p => ({ ...p, [f.key === 'new' ? 'newPwd' : f.key]: e.target.value }))}
                        placeholder="••••••••"
                        className="w-full px-3 py-2 pr-9 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <button onClick={() => setShowPwd(s => ({ ...s, [f.key]: !(s as any)[f.key] }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {(showPwd as any)[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
                    <Lock size={14} /> Update Password
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
